<template>
  <div class="login">
    <aside class="login-brand">
      <div class="brand-top">
        <img class="brand-logo" src="@/assets/imgs/rm.svg" alt="Automotriz RM" />
        <div>
          <span class="brand-name">Automotriz RM</span>
          <span class="brand-caption">GESTIÓN DE TALLER</span>
        </div>
      </div>

      <div class="brand-body">
        <p class="brand-headline">Cada auto, de la recepción a la entrega.</p>
        <p class="brand-lead">Órdenes, refacciones, caja y reportes del taller en un solo lugar.</p>

        <ol class="flow" aria-label="Flujo del taller">
          <li v-for="step in flujo" :key="step.name" class="flow-step" :class="step.tone">
            <span class="flow-icon"><AppIcon :name="step.icon" size="h-5 w-5" /></span>
            <span class="flow-name">{{ step.name }}</span>
            <span class="flow-hint">{{ step.hint }}</span>
          </li>
        </ol>
      </div>

      <p class="brand-foot">© {{ year }} Automotriz RM</p>
    </aside>

    <main class="login-main">
      <form class="login-card" novalidate data-testid="login-form" @submit.prevent="logIn">
        <h1>Iniciar sesión</h1>
        <p class="login-lead">Entra con tu usuario del sistema.</p>

        <label class="field-label" for="usuario">Usuario</label>
        <div class="field">
          <AppIcon name="user" size="h-5 w-5" class="field-icon" />
          <input
            id="usuario"
            ref="usuario"
            :value="user"
            name="username"
            autocomplete="username"
            autocapitalize="characters"
            spellcheck="false"
            :aria-invalid="error && !user.trim() ? 'true' : 'false'"
            @input="user = $event.target.value.toUpperCase()"
          />
        </div>

        <label class="field-label" for="password">Contraseña</label>
        <div class="field">
          <AppIcon name="lock" size="h-5 w-5" class="field-icon" />
          <input
            id="password"
            ref="password"
            v-model="pass"
            name="password"
            autocomplete="current-password"
            :type="showPass ? 'text' : 'password'"
            :aria-invalid="error && !pass ? 'true' : 'false'"
            @keyup="detectCaps"
            @keydown="detectCaps"
          />
          <button
            type="button"
            class="field-toggle"
            :aria-label="showPass ? 'Ocultar contraseña' : 'Mostrar contraseña'"
            :aria-pressed="showPass ? 'true' : 'false'"
            @click="showPass = !showPass"
          >
            <AppIcon :name="showPass ? 'eye-off' : 'eye'" size="h-5 w-5" />
          </button>
        </div>
        <p v-if="capsLock" class="caps-hint">Bloq Mayús está activado.</p>

        <div v-if="error" class="login-error" role="alert" data-testid="login-error">
          <AppIcon name="exclamation" size="h-5 w-5" />
          <span>{{ error }}</span>
        </div>

        <button class="login-submit" type="submit" :disabled="loading" data-testid="login-submit">
          <span v-if="loading" class="spinner" aria-hidden="true"></span>
          <span>{{ loading ? 'Entrando…' : 'Entrar' }}</span>
          <AppIcon v-if="!loading" name="arrow-right" size="h-5 w-5" />
        </button>

        <p class="login-help">¿No puedes entrar? Pide a un administrador que restablezca tu contraseña.</p>
      </form>
    </main>
  </div>
</template>

<script>
/*
 * Login — pantalla de acceso.
 *
 * Mismo lenguaje que el shell nuevo: marino del menú a la izquierda, lienzo
 * claro y tarjeta blanca a la derecha. El panel de marca muestra el flujo del
 * taller (las mismas tres etapas que resume el dashboard) en lugar de una foto.
 *
 * Contrato preservado: sesión y token en sessionStorage, redirect a #/menu.
 */
import LoginService from '@/services/login'
import AppIcon from '@/components/ui/AppIcon.vue'
import { hardRedirect } from '@/utils/navigation'

export default {
  name: 'Login',
  components: { AppIcon },
  data() {
    return {
      user: '',
      pass: '',
      error: '',
      loading: false,
      showPass: false,
      capsLock: false,
      year: new Date().getFullYear(),
      flujo: [
        { name: 'Recepción', hint: 'Orden de servicio', icon: 'clipboard', tone: 'is-reception' },
        { name: 'Reparación', hint: 'Refacciones e inventario', icon: 'wrench', tone: 'is-repair' },
        { name: 'Entrega', hint: 'Cobro en punto de venta', icon: 'check', tone: 'is-ready' },
      ],
    }
  },
  mounted() {
    this.$refs.usuario.focus()
  },
  methods: {
    detectCaps(event) {
      if (event.getModifierState) this.capsLock = event.getModifierState('CapsLock')
    },
    logIn() {
      if (this.loading) return
      if (!this.user.trim() || !this.pass) {
        this.error = 'Escribe tu usuario y tu contraseña.'
        this.$refs[this.user.trim() ? 'password' : 'usuario'].focus()
        return
      }
      this.loading = true
      this.error = ''
      LoginService.login(this.user.trim(), this.pass).then((res) => {
        this.loading = false
        if (res && res.status === 200 && res.data && res.data.token) {
          sessionStorage.setItem('sesion', JSON.stringify(res.data))
          sessionStorage.setItem('token', JSON.stringify(res.data.token))
          hardRedirect('#/menu')
          return
        }
        this.error = !res
          ? 'No hay conexión con el servidor. Intenta de nuevo.'
          : (res.data && res.data.message) || 'No se pudo iniciar sesión. Revisa usuario y contraseña.'
        this.pass = ''
        this.$refs.password.focus()
      })
    },
  },
}
</script>

<style scoped>
.login { display: grid; min-height: 100dvh; grid-template-columns: 1fr; background: #f3f5f8; color: #0b1b2e; font-family: Inter, Roboto, sans-serif; }

/* Panel de marca: en móvil es una franja; en escritorio, la columna izquierda */
.login-brand { position: relative; display: flex; flex-direction: column; gap: 20px; padding: 20px 20px 56px; overflow: hidden; background: linear-gradient(150deg, #102a43 0%, #12304f 55%, #174a76 100%); color: #d9e5ef; }
.login-brand::before { content: ''; position: absolute; inset: 0; background-image: linear-gradient(rgba(255, 255, 255, .045) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, .045) 1px, transparent 1px); background-size: 36px 36px; -webkit-mask-image: radial-gradient(ellipse at 70% 40%, #000 10%, transparent 75%); mask-image: radial-gradient(ellipse at 70% 40%, #000 10%, transparent 75%); pointer-events: none; }
.login-brand > * { position: relative; }
.brand-top { display: flex; align-items: center; gap: 12px; }
.brand-logo { width: 48px; height: 48px; flex: none; padding: 3px; border-radius: 12px; background: #fff; object-fit: contain; }
.brand-name { display: block; color: #fff; font-size: 15px; font-weight: 700; line-height: 1.15; }
.brand-caption { display: block; margin-top: 5px; color: #99b5cc; font-size: 10px; font-weight: 700; letter-spacing: .14em; }
.brand-body, .brand-foot { display: none; }

/* Tarjeta del formulario */
.login-main { display: flex; align-items: flex-start; justify-content: center; padding: 0 16px 40px; }
.login-card { width: 100%; max-width: 420px; margin-top: -32px; padding: 28px 22px 24px; border: 1px solid #e4e9ef; border-radius: 18px; background: #fff; box-shadow: 0 12px 32px rgba(16, 42, 67, .08); animation: rise .35s ease-out both; }
.login-card h1 { margin: 0; color: #102a43; font-size: 24px; font-weight: 700; line-height: 1.3; letter-spacing: -.015em; }
.login-lead { margin: 6px 0 22px; color: #526273; font-size: 15px; line-height: 1.45; }

.field-label { display: block; margin: 0 0 7px; color: #3d4b5f; font-size: 14px; font-weight: 600; }
.field { position: relative; margin-bottom: 16px; }
.field-icon { position: absolute; top: 50%; left: 14px; color: #758ca2; transform: translateY(-50%); pointer-events: none; }
.field input { width: 100%; height: 50px; padding: 0 48px 0 44px; border: 1px solid #d8e0ea; border-radius: 11px; background: #fff; color: #0b1b2e; font: inherit; font-size: 16px; outline: none; transition: border-color .15s, box-shadow .15s; }
.field input:hover { border-color: #b7c5d5; }
.field input:focus { border-color: #0a4f9c; box-shadow: 0 0 0 4px rgba(10, 79, 156, .14); }
.field input:focus ~ .field-icon, .field:focus-within .field-icon { color: #0a4f9c; }
.field input[aria-invalid='true'] { border-color: #b91c1c; }
#usuario { letter-spacing: .03em; }
.field-toggle { position: absolute; top: 50%; right: 6px; display: grid; width: 38px; height: 38px; place-items: center; border: 0; border-radius: 9px; background: transparent; color: #758ca2; cursor: pointer; transform: translateY(-50%); }
.field-toggle:hover { background: #edf3f8; color: #102a43; }
.field-toggle:focus-visible, .login-submit:focus-visible { outline: 3px solid #7ab9f2; outline-offset: 2px; }
.caps-hint { margin: -8px 0 16px; color: #92400e; font-size: 13px; font-weight: 600; }

.login-error { display: flex; align-items: flex-start; gap: 9px; margin: 2px 0 16px; padding: 11px 13px; border: 1px solid #f2c6c9; border-radius: 11px; background: #fff8f8; color: #8d232d; font-size: 14px; font-weight: 500; line-height: 1.4; }
.login-error svg { flex: none; margin-top: -1px; }

.login-submit { display: flex; width: 100%; height: 50px; align-items: center; justify-content: center; gap: 8px; margin-top: 6px; border: 0; border-radius: 11px; background: #0a4f9c; color: #fff; font: inherit; font-size: 16px; font-weight: 700; cursor: pointer; box-shadow: 0 6px 16px rgba(10, 79, 156, .22); transition: background .15s, box-shadow .15s, transform .15s; }
.login-submit:hover:not(:disabled) { background: #083f7d; box-shadow: 0 8px 20px rgba(10, 79, 156, .28); }
.login-submit:hover:not(:disabled) svg { transform: translateX(2px); }
.login-submit svg { transition: transform .15s; }
.login-submit:active:not(:disabled) { transform: translateY(1px); }
.login-submit:disabled { cursor: wait; opacity: .85; }
.spinner { width: 18px; height: 18px; border: 2px solid rgba(255, 255, 255, .35); border-top-color: #fff; border-radius: 50%; animation: spin .7s linear infinite; }

.login-help { margin: 18px 0 0; color: #607187; font-size: 13px; line-height: 1.45; text-align: center; }

@keyframes spin { to { transform: rotate(360deg); } }
@keyframes rise { from { opacity: 0; transform: translateY(8px); } }

@media (min-width: 1024px) {
  .login { grid-template-columns: minmax(440px, 5fr) 6fr; }
  .login-brand { justify-content: space-between; gap: 32px; padding: 40px clamp(40px, 4.5vw, 72px); }
  .brand-logo { width: 54px; height: 54px; }
  .brand-body, .brand-foot { display: block; }
  .brand-headline { max-width: 460px; margin: 0; color: #fff; font-size: clamp(32px, 2.8vw, 42px); font-weight: 700; line-height: 1.12; letter-spacing: -.025em; }
  .brand-lead { max-width: 420px; margin: 16px 0 0; color: #c1dcf2; font-size: 17px; line-height: 1.5; }
  .brand-foot { margin: 0; color: #88a8c1; font-size: 13px; }

  .flow { display: grid; max-width: 520px; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; margin: 40px 0 0; padding: 0; list-style: none; }
  .flow-step { position: relative; display: flex; flex-direction: column; gap: 4px; padding: 16px 14px; border: 1px solid rgba(255, 255, 255, .1); border-radius: 16px; background: rgba(255, 255, 255, .05); animation: rise .4s ease-out both; }
  .flow-step:nth-child(2) { animation-delay: .08s; }
  .flow-step:nth-child(3) { animation-delay: .16s; }
  /* Conector entre etapas */
  .flow-step:not(:last-child)::after { content: ''; position: absolute; top: 37px; right: -13px; width: 14px; height: 2px; background: rgba(162, 192, 226, .45); }
  .flow-icon { display: grid; width: 42px; height: 42px; margin-bottom: 8px; place-items: center; border-radius: 12px; }
  .is-reception .flow-icon { color: #9a5b00; background: #fff3dd; }
  .is-repair .flow-icon { color: #0a4f9c; background: #eaf2fb; }
  .is-ready .flow-icon { color: #047857; background: #e3f5ed; }
  .flow-name { color: #fff; font-size: 15px; font-weight: 700; }
  .flow-hint { color: #a9c2d8; font-size: 13px; line-height: 1.35; }

  .login-main { align-items: center; padding: 40px; }
  .login-card { margin-top: 0; padding: 36px 34px 28px; }
  .login-card h1 { font-size: 26px; }
}

@media (prefers-reduced-motion: reduce) {
  .login-card, .flow-step { animation: none; }
  .spinner { animation-duration: 1.6s; }
}
</style>
